<script setup lang="ts">
import { computed, ref, useId, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { deepEqual } from 'ts-deep-utils'
import { useQuery } from '@/composables/api'
import { useCreditorSettings } from '@/composables/useCreditorSettings'
import { useResourceSwaggerDefinition, getPropertySchema, isReferenceObject } from '@/composables/useSwagger'
import { deepToRaw } from '@/utils/vue'
import BDialog from '@/components/base/BDialog/BDialog.vue'
import BSelect from '@/components/base/BSelect/BSelect.vue'
import type { components } from '@/types/_api'

type CreditorSettings = components['schemas']['Creditor-Settings']
type NotificationTypes = NonNullable<CreditorSettings['settings']['notification_types']>
type Creditor = components['schemas']['Creditor']

interface SettingState {
  switch: boolean
  creditors: string[]
}

defineProps<{
  description?: string
}>()

const model = defineModel<boolean>({ default: false })
const { t } = useI18n()
const formId = useId()

const { data: creditorsData, isLoading: isLoadingCreditors } = useQuery(
  '/creditors',
  {
    getAll: true,
    query: {
      where: { is_active: true },
      sort: 'name',
      projection: { name: 1, _id: 1 },
    },
  },
  { enabled: model }
)

const creditors = computed<Creditor[]>(() => creditorsData.value?._items ?? [])
const creditorIds = computed(() => creditors.value.map((c) => c._id))
const hasMultipleCreditors = computed(() => creditors.value.length > 1)

const {
  isLoading: isLoadingCreditorSettings,
  isPending,
  serverCreditorSettings,
  bulkUpdateSettings,
  saveCreditorSettingsImmediately,
} = useCreditorSettings()

const { data: swaggerDefinition, isLoading: isLoadingSchema } = useResourceSwaggerDefinition('creditor--settings')

const notificationTypeKeys = computed<string[]>(() => {
  const schema = swaggerDefinition.value
  if (schema == null) return []
  try {
    const notificationTypesSchema = getPropertySchema(schema, 'settings.notification_types')
    if (isReferenceObject(notificationTypesSchema) || notificationTypesSchema.type !== 'object') return []
    return Object.keys(notificationTypesSchema.properties ?? {})
  } catch (error) {
    console.warn('Failed to parse notification types schema:', error)
    return []
  }
})

const isLoading = computed(() => isLoadingCreditors.value || isLoadingSchema.value || isLoadingCreditorSettings.value)

const localState = ref<Record<string, SettingState>>({})
const oldState = ref<Record<string, SettingState>>({})

function initLocalState() {
  const state: Record<string, SettingState> = {}

  for (const key of notificationTypeKeys.value) {
    state[key] = { switch: false, creditors: [] }
  }

  for (const creditorId of creditorIds.value) {
    const creditorSettings = serverCreditorSettings(creditorId)

    for (const key of notificationTypeKeys.value) {
      const isEnabled =
        creditorSettings?.settings.notification_types?.[key as keyof NotificationTypes]?.enabled === true
      const keyState = state[key]
      if (isEnabled && keyState !== undefined) {
        keyState.creditors.push(creditorId)
        keyState.switch = true
      }
    }
  }

  localState.value = state
  oldState.value = deepToRaw(state)
}

watch(
  [model, isLoading],
  ([isOpen, loading]) => {
    if (isOpen && !loading) {
      initLocalState()
    }
  },
  { immediate: true }
)

function getSettingSwitch(key: string): boolean {
  return localState.value[key]?.switch ?? false
}

function getSettingCreditors(key: string): string[] {
  return localState.value[key]?.creditors ?? []
}

const hasChanges = computed(() => {
  return !deepEqual(localState.value, oldState.value)
})

function onToggle(key: string, newValue: boolean) {
  localState.value[key] ??= { switch: false, creditors: [] }
  localState.value[key].switch = newValue
  if (newValue) {
    localState.value[key].creditors = creditorIds.value.slice()
  } else {
    localState.value[key].creditors = []
  }
}

function onCreditorsChange(key: string, newCreditors: string[]) {
  localState.value[key] ??= { switch: false, creditors: [] }
  localState.value[key].creditors = newCreditors
}

// Set-based diff keeps per-type comparison O(N+M); batching yields between
// types so the main thread stays responsive on large accounts.
const SAVE_BATCH_SIZE = 200

async function save() {
  const operations: { creditorId: string; dottedPath: string; value: unknown }[] = []
  const now = new Date()

  for (const key of notificationTypeKeys.value) {
    const oldSet = new Set(oldState.value[key]?.creditors ?? [])
    const newSet = new Set(localState.value[key]?.creditors ?? [])

    const dottedPath = `notification_types.${key}`

    for (const creditorId of newSet) {
      if (!oldSet.has(creditorId)) {
        operations.push({ creditorId, dottedPath, value: { enabled: true, from: now } })
      }
    }
    for (const creditorId of oldSet) {
      if (!newSet.has(creditorId)) {
        operations.push({ creditorId, dottedPath, value: { enabled: false, from: now } })
      }
    }

    if (operations.length > SAVE_BATCH_SIZE) {
      await new Promise((resolve) => setTimeout(resolve, 0))
    }
  }

  bulkUpdateSettings(operations)

  await saveCreditorSettingsImmediately()
  oldState.value = deepToRaw(localState.value)
  model.value = false
}
</script>

<template>
  <b-dialog
    v-model="model"
    size="medium"
    :confirm-on-close="hasChanges"
  >
    <template #title>{{ t('common.adjust_creditor_notifications') }}</template>

    <v-progress-linear
      v-if="isLoading"
      indeterminate
    />

    <v-form
      v-if="!isLoading"
      :id="formId"
      @submit.prevent="save"
    >
      <v-card-text>
        <div class="mb-4">
          {{ description }}
        </div>

        <div
          v-for="(key, index) in notificationTypeKeys"
          :key="key"
        >
          <div class="d-flex justify-space-between align-center">
            <label
              :for="`notification-switch-${key}`"
              class="text-body-large font-weight-medium text-medium-emphasis"
            >
              {{ t(`common.${key}`) }}
            </label>
            <v-switch
              :id="`notification-switch-${key}`"
              :model-value="getSettingSwitch(key)"
              :disabled="isPending"
              color="brand-primary"
              hide-details
              class="ma-0 flex-grow-0"
              @update:model-value="onToggle(key, $event as boolean)"
            />
          </div>

          <b-select
            v-if="getSettingSwitch(key) && hasMultipleCreditors"
            :model-value="getSettingCreditors(key)"
            :items="creditors"
            :label="t('common.select_creditors')"
            :disabled="isPending"
            item-title="name"
            item-value="_id"
            multiple
            clearable
            class="mt-2"
            @update:model-value="onCreditorsChange(key, $event as string[])"
          />

          <v-divider
            v-if="index < notificationTypeKeys.length - 1"
            class="my-4"
          />
        </div>
      </v-card-text>
    </v-form>

    <template #footer="{ closeProps }">
      <v-btn
        :disabled="isPending"
        variant="text"
        color="primary"
        v-bind="closeProps"
      >
        {{ t('common.cancel') }}
      </v-btn>
      <v-btn
        type="submit"
        :form="formId"
        :disabled="!hasChanges"
        :loading="isPending"
        color="primary"
        variant="flat"
      >
        {{ t('common.save') }}
      </v-btn>
    </template>
  </b-dialog>
</template>
